'use client'

import { AnimatePresence, m, useMotionValueEvent, useScroll } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import React, { useEffect, useState } from 'react'

import { COMPANY_ADDRESS, COMPANY_PHONE } from '@/lib/seo'

const navLinks = [
  { name: 'Услуги', href: '/services', testId: 'nav-link-services' },
  { name: 'Направления', href: '/directions', testId: 'nav-link-directions' },
  { name: 'Кейсы', href: '/cases', testId: 'nav-link-cases' },
  { name: 'Агентство', href: '/agency', testId: 'nav-link-agency' },
  { name: 'Новости', href: '/news', testId: 'nav-link-news' },
  { name: 'Контакты', href: '/contacts', testId: 'nav-link-contacts' },
]

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [hidden, setHidden] = useState(false)
  const { scrollY } = useScroll()
  const pathname = usePathname()
  const router = useRouter()

  useMotionValueEvent(scrollY, 'change', (latest) => {
    const previous = scrollY.getPrevious() ?? latest
    const delta = latest - previous

    if (latest <= 120) {
      setHidden(false)
      return
    }

    // Hysteresis removes flicker and makes header return smoothly on upward scroll.
    if (delta > 4.5 && latest > 220) {
      setHidden(true)
      return
    }
    if (delta < -2.8) {
      setHidden(false)
    }
  })

  function closeMobileMenu() {
    setIsOpen(false)
  }

  function prefetchRoute(href: string) {
    try {
      router.prefetch(href)
    } catch {
      // no-op
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  return (
    <>
      <m.header
        variants={{
          visible: { y: 0, opacity: 1 },
          hidden: { y: '-115%', opacity: 0.96 },
        }}
        animate={hidden ? 'hidden' : 'visible'}
        transition={{ type: 'spring', stiffness: 380, damping: 42, mass: 0.88 }}
        className="pointer-events-none fixed top-0 left-0 z-[90] flex w-full transform-gpu justify-center px-3 py-3 [@media(max-height:820px)]:py-2 [@media(max-height:980px)]:py-2.5"
      >
        <div className="pointer-events-auto flex w-[96vw] max-w-[1760px] items-center justify-between gap-2 sm:gap-4">
          <Link
            href="/"
            className="group relative z-50 max-w-[calc(100vw-5.7rem)] min-w-0 shrink rounded-2xl border border-white/10 bg-[#040a18]/50 px-3 py-2.5 shadow-[0_10px_35px_rgba(2,6,23,0.45)] backdrop-blur-xl transition-all duration-500 sm:max-w-none sm:px-4 sm:py-3"
            data-testid="nav-link-home"
            onMouseEnter={() => prefetchRoute('/')}
            onFocus={() => prefetchRoute('/')}
          >
            <span className="font-display relative block truncate text-[1.26rem] leading-none font-black tracking-[-0.03em] text-white uppercase transition-opacity hover:opacity-85 sm:text-[2rem] md:text-[2.2rem] [@media(max-height:820px)]:md:text-[1.82rem] [@media(max-height:980px)]:md:text-[2.02rem] [@media(max-width:370px)]:text-[1.12rem]">
              Цифровой горизонт
            </span>
          </Link>

          <div className="hidden xl:flex xl:flex-1 xl:justify-end">
            <nav className="relative flex items-center gap-1 rounded-full border border-white/12 bg-[#091124]/74 px-2.5 py-2.5 shadow-[0_14px_42px_rgba(3,7,18,0.52)] backdrop-blur-xl">
              <div className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              {navLinks.map((link) => {
                const isActive =
                  (pathname.startsWith(link.href) && link.href !== '/') || pathname === link.href

                return (
                  <Link
                    key={link.name}
                    href={link.href}
                    data-testid={link.testId}
                    onClick={closeMobileMenu}
                    onMouseEnter={() => prefetchRoute(link.href)}
                    onFocus={() => prefetchRoute(link.href)}
                    className="relative rounded-full px-5 py-2.5 text-[1.05rem] font-semibold tracking-[0.01em] text-gray-400 transition-colors hover:text-white [@media(max-height:820px)]:px-3.5 [@media(max-height:820px)]:py-1.5 [@media(max-height:820px)]:text-[0.92rem] [@media(max-height:980px)]:px-4 [@media(max-height:980px)]:py-2 [@media(max-height:980px)]:text-[0.98rem]"
                  >
                    {isActive && (
                      <m.div
                        layoutId="nav-pill"
                        className="absolute inset-0 rounded-full border border-white/25 bg-[linear-gradient(135deg,rgba(255,255,255,0.2),rgba(148,163,184,0.07))] shadow-[0_8px_26px_rgba(15,23,42,0.35)]"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className={`relative z-10 ${isActive ? 'text-white' : ''}`}>
                      {link.name}
                    </span>
                  </Link>
                )
              })}
            </nav>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`z-50 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-[#081124]/80 text-white shadow-[0_8px_26px_rgba(2,6,23,0.45)] backdrop-blur-xl transition-opacity duration-300 sm:h-12 sm:w-12 xl:hidden ${
              isOpen ? 'pointer-events-none opacity-0' : 'opacity-100'
            }`}
            aria-label={isOpen ? 'Закрыть меню' : 'Открыть меню'}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </m.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[110] bg-[#020617]/72 backdrop-blur-md xl:hidden"
            onClick={closeMobileMenu}
          >
            <m.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="ml-auto flex h-full w-full flex-col border-l border-white/10 bg-[linear-gradient(180deg,#071021_0%,#050b18_100%)] p-5 shadow-[-20px_0_60px_rgba(2,6,23,0.7)] sm:w-[min(90vw,430px)] sm:p-6"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-6 flex items-center justify-between">
                <div className="home-meta text-gray-400">Навигация</div>
                <button
                  onClick={closeMobileMenu}
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] text-white"
                  aria-label="Закрыть меню"
                >
                  <X size={22} />
                </button>
              </div>

              <nav className="flex flex-1 flex-col gap-2">
                {navLinks.map((link, i) => {
                  const isActive =
                    (pathname.startsWith(link.href) && link.href !== '/') || pathname === link.href
                  return (
                    <m.div
                      key={link.name}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 24 }}
                      transition={{ delay: 0.03 + i * 0.045, duration: 0.3, ease: 'easeOut' }}
                    >
                      <Link
                        href={link.href}
                        data-testid={`mobile-${link.testId}`}
                        onClick={closeMobileMenu}
                        onMouseEnter={() => prefetchRoute(link.href)}
                        onFocus={() => prefetchRoute(link.href)}
                        className={`block rounded-2xl border px-4 py-3 text-[1.08rem] font-semibold tracking-[0.01em] transition-colors ${
                          isActive
                            ? 'border-white/25 bg-white/[0.14] text-white'
                            : 'border-white/10 bg-white/[0.03] text-gray-300 hover:bg-white/[0.08] hover:text-white'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </m.div>
                  )
                })}
              </nav>

              <div className="border-t border-white/10 pt-5 text-sm text-gray-400">
                <p>{`г. ${COMPANY_ADDRESS.city}, ${COMPANY_ADDRESS.street}`}</p>
                <p className="mt-1">{COMPANY_PHONE}</p>
              </div>
            </m.aside>
          </m.div>
        )}
      </AnimatePresence>
    </>
  )
}
